import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { platform } from '../lib/api.js'
import { Badge, Card, Empty, Spinner } from '../components/ui/index.js'
import { useI18n } from '../lib/i18n.js'

/**
 * Plataforma → Ideias do concierge.
 *
 * Conversas do chat público da página institucional (asknamba.com): a IA
 * recolhe o que as pessoas querem facilitar e, ao concluir, resume cada
 * conversa em summary/tags/ideas — o painel deixa os padrões visíveis.
 */
export function PlatformIdeas() {
  const { t } = useI18n()
  const [openId, setOpenId] = useState<string | null>(null)

  const { data: conversations = [], isLoading } = useQuery({
    queryKey: ['platform-concierge'],
    queryFn: platform.conciergeConversations,
  })

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Card title={t('admin.ideas.title')} description={t('admin.ideas.desc')}>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner className="w-5 h-5 text-indigo-400" />
          </div>
        ) : conversations.length === 0 ? (
          <Empty title={t('admin.ideas.empty_title')} description={t('admin.ideas.empty_hint')} />
        ) : (
          <ul className="divide-y divide-gray-100">
            {conversations.map((conv) => {
              const tags = conv.tags ? (JSON.parse(conv.tags) as string[]) : []
              const ideas = conv.ideas ? (JSON.parse(conv.ideas) as string[]) : []
              const open = openId === conv.id
              return (
                <li key={conv.id} className="py-4">
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Badge color="gray">{conv.locale}</Badge>
                        {conv.finishedAt ? (
                          tags.map((tag) => (
                            <Badge key={tag} color="blue">
                              {tag}
                            </Badge>
                          ))
                        ) : (
                          <Badge color="yellow">{t('admin.ideas.no_summary')}</Badge>
                        )}
                        <span className="text-xs text-gray-400">
                          {new Date(conv.createdAt * 1000).toLocaleString()} ·{' '}
                          {t('admin.ideas.messages')}: {conv.messageCount}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">
                        {conv.summary ?? t('admin.ideas.no_summary')}
                      </p>
                      {ideas.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {ideas.map((idea) => (
                            <li key={idea} className="text-sm text-gray-600">
                              💡 {idea}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <button
                      onClick={() => setOpenId(open ? null : conv.id)}
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-800 whitespace-nowrap"
                    >
                      {t(open ? 'admin.ideas.hide' : 'admin.ideas.show')}
                    </button>
                  </div>
                  {open && <ConversationMessages id={conv.id} />}
                </li>
              )
            })}
          </ul>
        )}
      </Card>
    </div>
  )
}

function ConversationMessages({ id }: { id: string }) {
  const { t } = useI18n()
  const { data, isLoading } = useQuery({
    queryKey: ['platform-concierge', id],
    queryFn: () => platform.conciergeConversation(id),
    staleTime: 5 * 60_000,
  })

  if (isLoading) {
    return (
      <div className="flex justify-center py-3">
        <Spinner className="w-4 h-4 text-indigo-400" />
      </div>
    )
  }
  if (!data) return null

  return (
    <div className="mt-3 rounded-xl border border-gray-100 bg-gray-50 p-3 space-y-2">
      {data.messages.map((m) => (
        <p
          key={m.id}
          className={m.role === 'user' ? 'text-sm text-gray-800' : 'text-sm text-gray-500 italic'}
        >
          <strong className="mr-1 not-italic">{m.role === 'user' ? '👤' : '🤖'}</strong>
          {m.content}
        </p>
      ))}
      <p className="text-xs text-gray-400 pt-1">{t('admin.ideas.messages')}</p>
    </div>
  )
}
